import React from "react"
import { Field, FieldProps } from "react-final-form"

import { DefaultFieldProps } from "components/form/shared/default-field"
import { Paragraph } from "components/text"
import * as RadioInput from "./styles"

type RadioValueType = string | number | boolean

function RadioBox({ input, label, dataTest, onChange }: DefaultFieldProps<RadioValueType>) {
  const id = `${input.name}--${input.value}`

  return (
    <RadioInput.Label htmlFor={id}>
      <RadioInput.Input
        id={id}
        {...input}
        value={input.value.toString()}
        onChange={e => {
          /* If custom onChange handler is passed we chain it to the default onChange handler */
          input.onChange(e)
          onChange && onChange()
        }}
        data-test={dataTest}
      />
      <RadioInput.CheckMark />
      {/* Label might include a <Tooltip /> (which includes a div) so we need as="div" to prevent illegal DOM nesting */}
      <Paragraph ml={4} bold={input.checked} as="div">
        {label}
      </Paragraph>
    </RadioInput.Label>
  )
}

/**
 * The format of the values stored in the internal final-form state is different from the format of those values in the inputs
 *
 * @returns the parsed value of correct type to be stored in the final-form internal state
 */
const parse = (value: string): RadioValueType => {
  if (value === "true") {
    return true
  }

  if (value === "false") {
    return false
  }

  const numberRegexp = /^[0-9]+$/
  if (numberRegexp.test(value)) {
    return parseInt(value, 10)
  }

  return value
}

export function RadioInputField(props: FieldProps<RadioValueType, HTMLInputElement>) {
  return (
    <Field type="radio" px={0} py={0} parse={parse} {...props}>
      {(inputProps: DefaultFieldProps<RadioValueType>) => <RadioBox {...inputProps} dataTest={props.dataTest} />}
    </Field>
  )
}

export default RadioInputField
